import { useEffect, useState } from "react";
import { Head, router } from "@inertiajs/react";
import { BarChart3, Building2, CheckCircle2, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import FiltersPanel from "@/components/admin/school_prospects/FiltersPanel";
import ForecastPanel, { DEFAULT_PRICING } from "@/components/admin/school_prospects/ForecastPanel";
import ProspectsTable from "@/components/admin/school_prospects/ProspectsTable";
import StatCard from "@/components/admin/StatCard";
import { Button } from "@/components/ui/button";

export default function Index({
  prospects,
  forecast_schools,
  filters,
  divisions,
  divisions_by_province,
  provinces,
  grade_options,
  interest_options,
  forecast_open,
  stats,
  pagination,
}) {
  const [draftFilters, setDraftFilters] = useState(filters);
  const [showForecast, setShowForecast] = useState(Boolean(forecast_open));
  const [pricing, setPricing] = useState(DEFAULT_PRICING);

  useEffect(() => setDraftFilters(filters), [filters]);
  useEffect(() => {
    if (forecast_open) setShowForecast(true);
  }, [forecast_open]);

  const queryFor = (next, page = 1) => {
    const query = {};
    if (next.q) query.q = next.q;
    if (next.province) query.province = next.province;
    const availableDivisions = next.province ? divisions_by_province[next.province] || [] : divisions;
    const selectedDivisions = availableDivisions.filter((division) => next.divisions.includes(division));
    if (selectedDivisions.length !== availableDivisions.length) {
      if (selectedDivisions.length > availableDivisions.length / 2) {
        query.excluded_divisions = availableDivisions.filter((division) => !selectedDivisions.includes(division));
      } else {
        query.divisions = selectedDivisions.length > 0 ? selectedDivisions : ["__none__"];
      }
    }
    if (next.grades.length > 0) query.grades = next.grades;
    if (next.grades.length > 0 && next.grade_mode !== "any") query.grade_mode = next.grade_mode;
    if (next.requires_council) query.requires_council = "1";
    if (next.requires_society) query.requires_society = "1";
    if (next.pipeline !== "all") query.pipeline = next.pipeline;
    if (next.min_enrollment !== null && next.min_enrollment !== "") query.min_enrollment = next.min_enrollment;
    if (next.max_enrollment !== null && next.max_enrollment !== "") query.max_enrollment = next.max_enrollment;
    if (next.contacted !== "all") query.contacted = next.contacted;
    if (next.interest_status) query.interest_status = next.interest_status;
    if (showForecast) query.forecast = "1";
    if (page > 1) query.page = page;
    return query;
  };

  const visit = (next, page = 1) => {
    router.get("/admin/school_prospects", queryFor(next, page), {
      preserveState: true,
      preserveScroll: true,
    });
  };

  const resetFilters = () => {
    const reset = {
      q: "",
      province: null,
      divisions: [...divisions],
      grades: [],
      grade_mode: "any",
      requires_council: false,
      requires_society: false,
      pipeline: "all",
      min_enrollment: null,
      max_enrollment: null,
      contacted: "all",
      interest_status: null,
    };
    setDraftFilters(reset);
    visit(reset);
  };

  const updateProspect = (prospect, attributes) => {
    router.patch(prospect.update_path, { school_prospect: attributes }, {
      preserveScroll: true,
      only: ["prospects", "forecast_schools", "stats", "pagination", "flash"],
    });
  };

  return (
    <AdminLayout
      eyebrow="Outreach"
      title="School prospects"
      actions={(
        <Button type="button" variant={showForecast ? "secondary" : "outline"} size="sm" onClick={() => setShowForecast(!showForecast)} aria-expanded={showForecast}>
          <BarChart3 className="h-4 w-4" /> {showForecast ? "Hide forecast" : "Show forecast"}
        </Button>
      )}
    >
      <Head title="Admin · School prospects" />

      <p className="text-sm text-muted-foreground -mt-6 mb-8 max-w-3xl">
        School outreach and opportunity forecasting across Alberta, British Columbia, Saskatchewan, and Manitoba.
      </p>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4 mb-8">
        <StatCard icon={<Building2 className="h-4 w-4" />} label="Schools" value={stats.total} color="sky" />
        <StatCard icon={<CheckCircle2 className="h-4 w-4" />} label="Contacted" value={stats.contacted} color="forest" />
        <StatCard icon={<Building2 className="h-4 w-4" />} label="Not contacted" value={stats.not_contacted} color="primary" />
        <StatCard icon={<Sparkles className="h-4 w-4" />} label="Promising" value={stats.promising} color="forest" />
      </div>

      <FiltersPanel
        filters={draftFilters}
        setFilters={setDraftFilters}
        divisions={divisions}
        divisionsByProvince={divisions_by_province}
        provinces={provinces}
        grades={grade_options}
        interestOptions={interest_options}
        onApply={() => visit({ ...draftFilters, q: draftFilters.q.trim() })}
        onReset={resetFilters}
      />

      {showForecast && <ForecastPanel schools={forecast_schools} pricing={pricing} setPricing={setPricing} />}

      <div className="flex items-center justify-between gap-4 mb-3 text-xs text-muted-foreground">
        <p>{pagination.total_count === 0 ? "No schools match these filters." : `Showing ${pagination.from}–${pagination.to} of ${pagination.total_count}`}</p>
        <p className="hidden sm:block">Status changes save immediately.</p>
      </div>

      <ProspectsTable prospects={prospects} interestOptions={interest_options} onUpdate={updateProspect} />

      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between gap-4 mt-5">
          <Button variant="outline" size="sm" disabled={pagination.current_page === 1} onClick={() => visit(filters, pagination.current_page - 1)}>
            <ChevronLeft className="h-4 w-4" /> Previous
          </Button>
          <span className="text-sm text-muted-foreground">Page {pagination.current_page} of {pagination.total_pages}</span>
          <Button variant="outline" size="sm" disabled={pagination.current_page === pagination.total_pages} onClick={() => visit(filters, pagination.current_page + 1)}>
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </AdminLayout>
  );
}
